import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { concatMap, map, shareReplay, switchMap, toArray } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { getContainerProjectId, getContainerType } from '../../shared/container.utils';
import { Container } from '../../shared/models/container.model';
import { FormDefinition } from '../../shared/models/project.model';
import { ViewerApp } from '../../shared/models/site-settings.model';
import { ViewerService } from '../../viewers/viewer.service';

@Component({
  selector: 'container-applications',
  templateUrl: './container-applications.component.html',
  styles: [`
    a.app-link {
      display: block;
      margin: 20px 0 0;
    }
  `],
})
export class ContainerApplicationsComponent implements OnChanges {
  @Input() container: Container
  @Input() appType = ''
  @Output() afterChange = new EventEmitter()

  public formApps$: Observable<ViewerApp[]>

  private infoForms$: Observable<FormDefinition[]>
  private settingForms$: Observable<FormDefinition[]>

  constructor(
    private coreApi: CoreApiService,
    private viewerService: ViewerService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.container?.currentValue) {
      const container = changes.container.currentValue;
      const projectId = getContainerProjectId(container);
      const containerType = getContainerType(container);
      this.infoForms$ = this.coreApi.projects.get(projectId).pipe(
        map(project => {
          const forms = project.info?.forms?.[containerType] as (FormDefinition[] | { [key: string]: FormDefinition });
          return Array.isArray(forms) ? forms : Object.entries(forms ?? {}).map(([key, form]) => ({ ...form, formId: key }));
        }),
        shareReplay(1),
      );
      this.settingForms$ = this.coreApi.projects.settings.get(projectId).pipe(
        map(settings => settings.forms?.[containerType] ?? []),
        shareReplay(1),
      );
    }
    if (this.container && (changes.container || changes.appType)) {
      const container = changes.container.currentValue;
      const projectId = getContainerProjectId(container);

      this.formApps$ = this.viewerService.getAssociatedApps({ container: container, projectId }).pipe(
        switchMap(apps => {
          const filteredApps = apps.filter(app => !this.appType || this.appType === app.type);
          if (this.appType === 'form') {
            return from(filteredApps).pipe(
              concatMap(app => {
                // If form definitions are defined, replace this app entry with each relevant form.
                const forms$ = app.options?.definitions === 'project.info'
                  ? this.infoForms$
                  : app.options?.definitions === 'project.settings'
                    ? this.settingForms$
                    : null;
                if (forms$) {
                  return forms$.pipe(
                    switchMap(forms => from(forms)),
                    map(form => {
                      return ({
                        ...app,
                        name: form.name,
                        options: {
                          ...app.options,
                          formId: form.key,
                        },
                      });
                    })
                  );
                }
                return of(app);
              }),
              toArray(),
            );
          }
          return of(filteredApps);
        }));
    }
  }

  openApp(app: ViewerApp): void {
    this.viewerService.view({
      app: app,
      container: this.container,
    }).subscribe(result => {
      if (result?.dirty) {
        this.afterChange.emit(null);
      }
    });
  }
}
